import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, User, ClipboardList, LayoutDashboard, MessageCircle, Home, X } from "lucide-react";
import Logo from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Debounce util
function useDebounce(value, delay) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

// Wrap matched substring in <mark> tags — safe because we only ever render
// plain-text product/category/weight fields from our own backend.
function highlight(text, needle) {
  const t = String(text ?? "");
  const n = String(needle ?? "").trim();
  if (!n) return t;
  const idx = t.toLowerCase().indexOf(n.toLowerCase());
  if (idx === -1) return t;
  return (
    <>
      {t.slice(0, idx)}
      <mark className="bg-yellow-200 text-brown font-semibold px-0.5 rounded-sm">
        {t.slice(idx, idx + n.length)}
      </mark>
      {t.slice(idx + n.length)}
    </>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [menuOpen, setMenuOpen] = useState(false);
  const [waNumber, setWaNumber] = useState("");
  const { itemCount } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchWrapRef = useRef(null);
  const reqIdRef = useRef(0);

  const debouncedQ = useDebounce(q, 150);

  useEffect(() => {
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => setWaNumber(r.data.whatsapp || "")).catch(() => {});
  }, []);

  // Live search — fires whenever debouncedQ changes.
  useEffect(() => {
    const term = debouncedQ.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      setActiveIdx(-1);
      return;
    }
    const rid = ++reqIdRef.current;
    setLoading(true);
    api.get(`/search`, { params: { q: term } })
      .then((r) => {
        if (rid !== reqIdRef.current) return; // stale response — ignore
        setResults(Array.isArray(r.data) ? r.data : []);
        setActiveIdx(-1);
      })
      .catch(() => {
        if (rid !== reqIdRef.current) return;
        setResults([]);
        setActiveIdx(-1);
      })
      .finally(() => {
        if (rid === reqIdRef.current) setLoading(false);
      });
  }, [debouncedQ]);

  // Close search on outside click / tap.
  useEffect(() => {
    if (!searchOpen) return;
    const onDocDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [searchOpen]);

  // Keep active item scrolled into view (keyboard nav).
  useEffect(() => {
    if (activeIdx < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(`[data-result-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIdx]);

  const goHome = (e) => {
    e?.preventDefault();
    setSearchOpen(false);
    if (loc.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      nav("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setQ("");
    setResults([]);
    setActiveIdx(-1);
  }, []);

  const pickResult = useCallback((row) => {
    if (!row) return;
    const params = new URLSearchParams();
    params.set("cat", row.category_slug);
    params.set("pid", row.product_id);
    params.set("size", row.weight_label);
    nav(`/?${params.toString()}`);
    closeSearch();
  }, [nav, closeSearch]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = activeIdx >= 0 ? results[activeIdx] : results[0];
      pickResult(target);
    }
  };

  const openSearch = () => {
    setSearchOpen((s) => {
      const next = !s;
      if (next) setTimeout(() => inputRef.current?.focus(), 50);
      else { setQ(""); setResults([]); setActiveIdx(-1); }
      return next;
    });
  };

  const term = debouncedQ.trim();
  const showDropdown = searchOpen && term.length > 0;

  const waLink = `https://wa.me/${(waNumber || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi Sri Durga Foods, I have a question.")}`;

  return (
    <header
      data-testid="site-header"
      className={`header-shrink fixed top-0 inset-x-0 z-40 bg-ivory border-b border-brown-border ${
        scrolled ? "shadow-[0_2px_16px_-8px_rgba(59,43,32,0.25)] py-1" : "py-2"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <button
            data-testid="header-home-btn"
            onClick={goHome}
            aria-label="Home"
            className="p-2 md:p-2.5 rounded-full hover:bg-ivory-soft text-brown transition-colors"
          >
            <Home className="w-5 h-5" strokeWidth={1.8} />
          </button>
          <Logo size={scrolled ? "sm" : "md"} className="-my-3 md:-my-4" />
        </div>

        <div className="flex items-center gap-1.5 md:gap-3">
          <button
            data-testid="header-search-btn"
            aria-label="Search"
            onClick={openSearch}
            className="p-2 md:p-2.5 rounded-full hover:bg-ivory-soft text-brown transition-colors"
          >
            <Search className="w-5 h-5" strokeWidth={1.8} />
          </button>

          <Link
            to="/cart"
            data-testid="header-cart-btn"
            aria-label="Cart"
            className="relative p-2 md:p-2.5 rounded-full hover:bg-ivory-soft text-brown transition-colors"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.8} />
            {itemCount > 0 && (
              <span data-testid="cart-badge" className="absolute -top-0.5 -right-0.5 bg-brand-red text-ivory text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {itemCount}
              </span>
            )}
          </Link>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="header-menu-btn"
                aria-label="Menu"
                className="p-2 md:p-2.5 rounded-full hover:bg-ivory-soft text-brown transition-colors"
              >
                <Menu className="w-5 h-5" strokeWidth={1.8} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ivory border-brown-border text-brown w-[85vw] sm:max-w-md p-0 overflow-y-auto">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-brown-border">
                <div className="flex justify-center">
                  <Logo size="md" asLink={false} />
                </div>
                <SheetTitle className="sr-only">Menu</SheetTitle>
              </SheetHeader>

              <nav className="px-4 py-5">
                <ul className="space-y-1">
                  <MenuLink to="/account" icon={User} label="Account" testid="menu-account" onClick={() => setMenuOpen(false)} />
                  <MenuLink to="/orders" icon={ClipboardList} label="Your Orders" testid="menu-your-orders" onClick={() => setMenuOpen(false)} />
                  {user?.role === "admin" && (
                    <MenuLink to="/secure-admin" icon={LayoutDashboard} label="Admin Dashboard" testid="menu-admin-dashboard" onClick={() => setMenuOpen(false)} />
                  )}
                  {user?.role === "employee_admin" && (
                    <MenuLink to="/admin" icon={ClipboardList} label="Check Orders" testid="menu-check-orders" onClick={() => setMenuOpen(false)} />
                  )}
                </ul>
                <div className="mt-6 pt-6 border-t border-brown-border">
                  {waLink ? (
                    <a
                      data-testid="menu-whatsapp"
                      href={waLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="w-full h-12 rounded-full bg-[#25D366] text-white font-semibold text-[13px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2 hover:brightness-95 transition-all"
                    >
                      <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                    </a>
                  ) : null}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {searchOpen && (
        <div ref={searchWrapRef} className="border-t border-brown-border bg-ivory animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-3">
            <div className="flex items-center gap-2 bg-white rounded-full border border-brown-border px-4 py-2.5">
              <Search className="w-4 h-4 text-brown-medium flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search products, category or size…"
                autoComplete="off"
                className="flex-1 bg-transparent outline-none text-brown placeholder:text-brown-light text-sm"
              />
              {q ? (
                <button
                  type="button"
                  data-testid="header-search-clear"
                  onClick={() => { setQ(""); setResults([]); setActiveIdx(-1); inputRef.current?.focus(); }}
                  aria-label="Clear search"
                  className="text-brown-medium hover:text-brown transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>

            {showDropdown && (
              <div
                ref={dropdownRef}
                data-testid="header-search-dropdown"
                role="listbox"
                className="mt-2 bg-white rounded-2xl border border-brown-border shadow-[0_16px_40px_-16px_rgba(59,43,32,0.35)] max-h-[70vh] overflow-y-auto"
              >
                {loading && results.length === 0 ? (
                  <div data-testid="header-search-loading" className="px-4 py-6 text-center text-brown-medium text-sm">
                    Searching…
                  </div>
                ) : results.length === 0 ? (
                  <div data-testid="header-search-empty" className="px-4 py-6 text-center text-brown-medium text-sm">
                    No products found for “{term}”.
                  </div>
                ) : (
                  <ul className="py-1">
                    {results.map((row, i) => (
                      <li key={`${row.product_id}:${row.weight_label}`}>
                        <button
                          type="button"
                          data-testid={`header-search-result-${i}`}
                          data-result-idx={i}
                          role="option"
                          aria-selected={i === activeIdx}
                          onMouseEnter={() => setActiveIdx(i)}
                          onClick={() => pickResult(row)}
                          className={`w-full flex items-center gap-3 px-3 md:px-4 py-2.5 text-left transition-colors ${
                            i === activeIdx ? "bg-ivory-soft" : "hover:bg-ivory-soft"
                          }`}
                        >
                          <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden bg-ivory-soft flex-shrink-0 border border-brown-border">
                            {row.image_url ? (
                              <img
                                src={row.image_url}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover"
                              />
                            ) : null}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-brown font-semibold text-sm truncate">
                              {highlight(row.product_name, term)}
                            </div>
                            <div className="text-brown-medium text-xs mt-0.5 truncate">
                              {highlight(row.category_name, term)}
                              <span className="mx-1.5 text-brown-light">·</span>
                              {highlight(row.weight_label, term)}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 pl-2">
                            {row.offer_price ? (
                              <>
                                <div className="text-brand-red font-bold text-sm">₹{row.offer_price}</div>
                                <div className="text-brown-light text-[11px] line-through">₹{row.price}</div>
                              </>
                            ) : (
                              <div className="text-brown font-bold text-sm">₹{row.price}</div>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MenuLink({ to, icon: Icon, label, testid, onClick }) {
  return (
    <li>
      <Link to={to} data-testid={testid} onClick={onClick} className="flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-white transition-colors group">
        <span className="w-11 h-11 rounded-full bg-white group-hover:bg-brand-red group-hover:text-ivory border border-brown-border inline-flex items-center justify-center text-brown transition-colors">
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </span>
        <span className="flex-1 text-brown font-semibold text-[15px]">{label}</span>
        <span className="text-brown-medium">›</span>
      </Link>
    </li>
  );
}
